import React, { useEffect, useState, useRef, useCallback } from "react";
import { useAppSelector, useAppDispatch } from '../redux/store';
import { fetchCallMetrics } from '../redux/callMetricsSlice';
import { setDateFilter, setTeamFilter, setSelectedUser, setUserIds } from '../redux/filterSlice';
import { format } from 'date-fns';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db, auth } from "../firebaseConfig";
import { useAuth } from "../AuthContext"
import { DateFilter } from '../types/filterTypes';
import getCurrentScreen from "../Helper/GetScreen";

const CallStatus: React.FC = () => {
    const { isAdmin, isManager, isStaff, userData, currentUser } = useAuth();
    const dispatch = useAppDispatch();
    const { metrics, loading, error } = useAppSelector(state => state.callMetrics);
    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);
    const dateFilter = useAppSelector(state => state.filter.dateFilter);
    const selectedUserId = useAppSelector(state => state.filter.selectedUserId);
    const teamFilter = useAppSelector(state => state.filter.selectedTeamFilter);

    // Company and user state
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [assignedToOptions, setAssignedToOptions] = useState<any[]>([]);
    const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null);

    // Filter state
    const [filterPopupOpen, setFilterPopupOpen] = useState(false);
    const [customDatePopupOpen, setCustomDatePopupOpen] = useState(false);
    const [selectedFilters, setSelectedFilters] = useState({ today: true, yesterday: false, custom: false });
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [teamSelectionMode, setTeamSelectionMode] = useState<'all' | 'individual'>('all');

    // Search state
    const [isSearchVisible, setIsSearchVisible] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedMember, setSelectedMember] = useState<string | null>(null);
    const [isMemberPopupOpen, setIsMemberPopupOpen] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);

    // Refs for click outside handling
    const filterPopupRef = useRef<HTMLDivElement>(null);
    const customDatePopupRef = useRef<HTMLDivElement>(null);
    const searchPopupRef = useRef<HTMLDivElement>(null);
    const memberPopupRef = useRef<HTMLDivElement>(null);

    console.log('Metrics:', metrics);
    
    const [statusItems, setStatusItems] = useState([
        { id: 1, label: "Attempted", value: metrics.attempted, color: "bg-blue-200" },
        { id: 2, label: "Missed Calls", value: metrics.missedCalls.toString(), color: "bg-red-300" },
        { id: 3, label: "Contacted", value: metrics.contacted.toString(), color: "bg-green-200" },
        { id: 4, label: "Inbound Calls", value: metrics.inboundCalls.toString(), color: "bg-purple-300" },
        { id: 5, label: "Total Call Duration", value: `${metrics.totalDuration}m`, color: "bg-black" },
        { id: 6, label: "Avg Call Duration", value: `${metrics.avgDuration}m`, color: "bg-black" },
    ]);

    useEffect(() => {
        setStatusItems([
            { id: 1, label: "Attempted", value: metrics.attempted, color: "bg-blue-200" },
            { id: 2, label: "Missed Calls", value: metrics.missedCalls.toString(), color: "bg-red-300" },
            { id: 3, label: "Contacted", value: metrics.contacted.toString(), color: "bg-green-200" },
            { id: 4, label: "Inbound Calls", value: metrics.inboundCalls.toString(), color: "bg-purple-300" },
            { id: 5, label: "Total Call Duration", value: `${metrics.totalDuration}m`, color: "bg-black" },
            { id: 6, label: "Avg Call Duration", value: `${metrics.avgDuration}m`, color: "bg-black" },
        ]);
    }, [metrics]);

    useEffect(() => {
        const fetchCompanyId = async () => {
            const user = auth.currentUser;
            if (user?.uid) {
                try {
                    const userDocRef = doc(db, "users", user.uid);
                    const userDocSnap = await getDoc(userDocRef);
                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data();
                        setCompanyId(userData.companyId);
                    }
                } catch (error) {
                    console.error("Error fetching company ID:", error);
                }
            }
        };
        fetchCompanyId();
    }, []);

    const initialFilterState = async () => {
        if (isAdmin && currentScreen === 'Admin') {
            dispatch(setTeamFilter('all'));
            dispatch(fetchCallMetrics({
                startDate: dateFilter.startDate,
                endDate: dateFilter.endDate,
                userId: teamFilter === 'individual' ? selectedUserId : null,
                userIds: []
            }));
            console.log('Admin dispatched');

        } else if (isManager && currentScreen === 'Manager') {
            // find all users that have the same managerId as the current user
            const usersQuery = query(
                collection(db, 'users'),
                where('companyId', '==', userData?.companyId),
                where('managerId', '==', currentUser?.uid)
            );
            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map(doc => doc.id);
            dispatch(setTeamFilter('managed'));
            dispatch(setUserIds(users));
            dispatch(fetchCallMetrics({
                startDate: dateFilter.startDate,
                endDate: dateFilter.endDate,
                userId: teamFilter === 'individual' ? selectedUserId : null,
                userIds: teamFilter === 'individual' ? [] : users
            }));

            console.log("Manager dispatched");

        } else if (isStaff && currentScreen === 'Staff') {
            dispatch(setTeamFilter('individual'));
            dispatch(setSelectedUser(currentUser?.uid ? currentUser.uid : null));
            dispatch(fetchCallMetrics({
                startDate: dateFilter.startDate,
                endDate: dateFilter.endDate,
                userId: currentUser?.uid ? currentUser.uid : null,
                userIds: []
            }));
            console.log('Staff dispatched');
        }
    };

    useEffect(() => {
        initialFilterState();
    }, [currentScreen]);


    // useEffect(() => {
    //     dispatch(fetchCallMetrics({
    //         startDate: dateFilter.startDate,
    //         endDate: dateFilter.endDate,
    //         userId: teamFilter === 'individual' ? selectedUserId : null
    //     }));
    // }, [dispatch, dateFilter.startDate, dateFilter.endDate, selectedUserId, teamFilter]);

    const fetchAssignedToOptions = useCallback(async () => {
        if (!companyId) return;
        try {
            let usersQuery = query(
                collection(db, "users"),
                where("companyId", "==", companyId)
            );

            if (isManager && currentScreen === 'Manager') {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where("managerIds", "array-contains", currentUser?.uid)
                );
            }

            const usersSnapshot = await getDocs(usersQuery);
            const users = usersSnapshot.docs.map((doc) => ({
                id: doc.id,
                name: doc.data().name
            }));
            setAssignedToOptions(users);
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    }, [companyId]);

    useEffect(() => {
        fetchAssignedToOptions();
    }, [fetchAssignedToOptions]);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (filterPopupRef.current && !filterPopupRef.current.contains(event.target as Node)) {
                setFilterPopupOpen(false);
            }
            if (customDatePopupRef.current && !customDatePopupRef.current.contains(event.target as Node)) {
                setCustomDatePopupOpen(false);
            }
            if (searchPopupRef.current && !searchPopupRef.current.contains(event.target as Node)) {
                setIsSearchVisible(false);
            }
            if (memberPopupRef.current && !memberPopupRef.current.contains(event.target as Node)) {
                setIsMemberPopupOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);


    const getFormattedDateRange = (start: Date, end: Date) => {
        return `${format(start, 'dd MMM yyyy')} - ${format(end, 'dd MMM yyyy')}`;
    };

    const fetchUserDetails = async (userId: string) => {
        if (!userId) return;
        try {
            const userDocRef = doc(db, "users", userId);
            const userDocSnap = await getDoc(userDocRef);
            if (userDocSnap.exists()) {
                setSelectedUserDetails(userDocSnap.data());
            }
        } catch (error) {
            console.error("Error fetching user details:", error);
        }
    };

    // Separated DB query into its own function for better reusability and testing
const fetchManagedUsers = async () => {
    const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', userData?.companyId),
        where('managerId', '==', currentUser?.uid)
    );
    const usersSnapshot = await getDocs(usersQuery);
    return usersSnapshot.docs.map(doc => doc.id);
};

    const handleApplyFilters = async () => {
        let filterData: DateFilter | null = null;

        if (selectedFilters.today) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            filterData = {
                type: 'today',
                startDate: today,
                endDate: new Date(),
            };
        } else if (selectedFilters.yesterday) {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            yesterday.setHours(0, 0, 0, 0);
            const yesterdayEnd = new Date(yesterday);
            yesterdayEnd.setHours(23, 59, 59, 999);
            filterData = {
                type: 'yesterday',
                startDate: yesterday,
                endDate: yesterdayEnd,
            };
        } else if (startDate && endDate) {
            const rangeStart = new Date(startDate);
            rangeStart.setHours(0, 0, 0, 0);
            const rangeEnd = new Date(endDate);
            rangeEnd.setHours(23, 59, 59, 999);
            filterData = {
                type: 'custom',
                startDate: rangeStart,
                endDate: rangeEnd,
            };
        }

        if (filterData) {
            dispatch(setDateFilter(filterData));
        }

        if (teamSelectionMode === 'all') {
            console.log("filter all--------------------------------------------");
            if (isAdmin && currentScreen === 'Admin') {
                // Admin sees all teams
                dispatch(setTeamFilter('all'));
                dispatch(setSelectedUser(null));
                dispatch(fetchCallMetrics({
                    startDate: filterData?.startDate || dateFilter.startDate,
                    endDate: filterData?.endDate || dateFilter.endDate,
                    userId: null,
                    userIds: []
                }));
            } else if (isManager && currentScreen === 'Manager') {
                // Manager sees their managed users
                const managedUsers = await fetchManagedUsers();
                dispatch(setTeamFilter('managed'));
                dispatch(setUserIds(managedUsers));
                dispatch(fetchCallMetrics({
                    startDate: filterData?.startDate || dateFilter.startDate,
                    endDate: filterData?.endDate || dateFilter.endDate,
                    userId: null,
                    userIds: managedUsers
                }));
            }
        } else if (teamSelectionMode === 'individual') {
            console.log("filter Individual--------------------------------------------");
            
            // Both conditions were identical, so combined them
            dispatch(setTeamFilter('individual'));
            dispatch(setSelectedUser(selectedMember));
            dispatch(setUserIds([]));
            dispatch(fetchCallMetrics({
                startDate: filterData?.startDate || dateFilter.startDate,
                endDate: filterData?.endDate || dateFilter.endDate,
                userId: selectedMember,
                userIds: []
            }));
        }

        setFilterPopupOpen(false);
        setCustomDatePopupOpen(false);
        setIsMemberPopupOpen(false);
    };


    const handleTeamSelectionChange = (mode: 'all' | 'individual') => {
        setTeamSelectionMode(mode);
        if (mode === 'all') {
            setSelectedMember(null);
            setSearchQuery('');
            setSelectedUserDetails(null);
        }
        if (mode === 'individual') {
            setIsMemberPopupOpen(true);
        }
    };

    const handleMemberSelection = (userId: string, userName: string) => {
        setSelectedMember(userId);
        setSearchQuery(userName);
        setShowDropdown(false);
        fetchUserDetails(userId);
    };


    if (error) {
        return <div className="text-red-500 p-4">{error}</div>;
    }
    const toggleMemberPopup = () => {
        setIsMemberPopupOpen(!isMemberPopupOpen);
    };
    const toggleFilterPopup = () => {
        setFilterPopupOpen(!filterPopupOpen);
    };
    const filteredOptions = assignedToOptions.filter(option =>
        option.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return (
        <div>
            <div className="mx-auto">
                {loading ? (
                    <div className="p-4">Loading metrics...</div>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center justify-center w-16 h-16 rounded-full"
                                    style={{ background: "linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)" }}>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/call-dropped.svg`}
                                        alt="Icon" className="w-10 h-10" />
                                </div>
                                <div className="flex items-center">
                                    <h2 className="text-2xl text-black font-medium"
                                        style={{ fontFamily: "Montserrat", lineHeight: "29.26px" }}>
                                        Outbound Call Status
                                    </h2>
                                    <span className="text-gray text-xs ml-2">
                                        ({startDate && endDate ? getFormattedDateRange(startDate, endDate) : 'Today'})
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-center gap-2">
                                <button onClick={() => setFilterPopupOpen(true)}>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/NavFilter.svg`}
                                        alt="Filter" className="w-7 h-7 cursor-pointer" />
                                </button>
                                {/* <button onClick={() => setIsSearchVisible(!isSearchVisible)}>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/NavSearch.svg`}
                                        alt="Search" className="w-7 h-7 cursor-pointer" />
                                </button> */}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                            {statusItems.map((item) => (
                                <div key={item.id}
                                    className="flex items-center bg-zinc-100 relative h-20 w-full"
                                    style={{ borderRadius: "32px 32px 32px 0" }}>
                                    <div className={`absolute left-0 rounded-tl-full rounded-br-full top-0 h-full w-6 bg-gradient-to-b ${item.color} to-transparent`} />
                                    <div className="flex items-center justify-between w-full px-4">
                                        <div className="ml-5">
                                            <span className="text-sm text-gray-600">{item.label}</span>
                                        </div>
                                        <div className="flex gap-2 items-center">
                                            <span className="font-medium text-gray-900">{item.value}</span>
                                            <div className="flex items-center justify-center w-10 h-10 bg-white rounded-full ml-1">
                                                <img src={`${process.env.PUBLIC_URL}/images/desktop/iconamoon_arrow-top.svg`}
                                                    alt="Status" className="w-6 h-6" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {filterPopupOpen && (
                            <div ref={filterPopupRef}
                                className="absolute top-16 left-1/2  transform -translate-x-1/2 w-[261px] h-[330px] bg-white border rounded-3xl shadow-lg p-4 z-50">
                                <div className="flex justify-between items-center">
                                    <h3
                                        className="text-[14px] font-normal leading-[20px] text-left"
                                        style={{
                                            fontFamily: "Montserrat",
                                            fontSize: "14px",
                                            fontWeight: "400",
                                            lineHeight: "20px",
                                        }} >Filters  </h3>
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                        alt="filters-black-icon"
                                        className="w-9 h-9" />
                                </div>
                                <div className="mt-2">
                                    <div className="mb-2">
                                        <span
                                            className="text-[12px] font-normal leading-[16px] text-left"
                                            style={{
                                                fontFamily: "Montserrat",
                                                fontSize: "12px",
                                                fontWeight: "400",
                                                lineHeight: "16px",
                                            }} >
                                            Date Range: </span>
                                        <div className="mt-2 text-[12px] font-normal">
                                            <label className="block mb-2 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="mr-2 cursor-pointer"
                                                    name="dateFilter"
                                                    checked={selectedFilters.today}
                                                    onChange={() =>
                                                        setSelectedFilters({
                                                            today: true,
                                                            yesterday: false,
                                                            custom: false,
                                                        })
                                                    }
                                                />
                                                Today
                                            </label>

                                            <label className="block cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="mr-2 cursor-pointer"
                                                    name="dateFilter"
                                                    checked={selectedFilters.yesterday}
                                                    onChange={() =>
                                                        setSelectedFilters({
                                                            today: false,
                                                            yesterday: true,
                                                            custom: false,
                                                        })
                                                    }
                                                />
                                                Yesterday
                                            </label>
                                        </div>

                                        <div className="mt-2 text-[12px] font-normal cursor-pointer">
                                            <label className="block cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    className="mr-2 cursor-pointer"
                                                    name="dateFilter"
                                                    checked={selectedFilters.custom}
                                                    onChange={() => {
                                                        setCustomDatePopupOpen(!selectedFilters.custom);
                                                        setSelectedFilters({
                                                            today: false,
                                                            yesterday: false,
                                                            custom: !selectedFilters.custom,
                                                        });
                                                    }}
                                                />
                                                {startDate && endDate
                                                    ? getFormattedDateRange(startDate, endDate)
                                                    : "Custom Date Range"}
                                            </label>
                                        </div>
                                    </div>

                                    {currentScreen !== 'Staff' && (
                                        <div className="mb-2">
                                            <span className="text-[14px] font-normal leading-[16px] text-left">
                                                Select Team Member:
                                            </span>
                                            <div className="mt-2 text-[12px] font-normal">
                                                <label className="block mb-2 cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="mr-2 cursor-pointer"
                                                        checked={teamSelectionMode === 'all'}
                                                        onChange={() =>
                                                            handleTeamSelectionChange(teamSelectionMode === 'all' ? 'individual' : 'all')
                                                        }
                                                    />
                                                    All Team
                                                </label>
                                                <label className="block cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        className="mr-2 cursor-pointer"
                                                        checked={teamSelectionMode === 'individual'}
                                                        onChange={() => handleTeamSelectionChange(teamSelectionMode === 'individual' ? 'all' : 'individual')} />
                                                    By Individual Member
                                                </label>
                                            </div>
                                        </div>
                                    )}
                                    <div className="flex justify-between space-x-3 mt-4">
                                        <button
                                            className="px-8 py-2 text-white bg-black rounded-full"
                                            onClick={toggleFilterPopup} > Cancel</button>
                                        <button
                                            className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                            onClick={handleApplyFilters}>  Apply </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        {customDatePopupOpen && (
                            <div ref={customDatePopupRef} className="absolute top-16 left-1/2 transform -translate-x-1/2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-50" >
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-[14px] font-normal leading-[20px] text-left"> Filters  </h3>
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/black-filter.svg`}
                                        alt="filters-black-icon"
                                        className="w-9 h-9" />   </div>
                                <div className="mb-1">
                                    <span className="text-[12px] font-normal leading-[16px] text-left">
                                        Select Date Range:
                                    </span>

                                    <div className="p-4 bg-white shadow-lg rounded-[12px]" style={{ width: '100%', height: 'auto' }}>
                                        <DatePicker
                                            selected={selectedDate}
                                            startDate={startDate || undefined}
                                            endDate={endDate || undefined}
                                            selectsRange
                                            onChange={(update) => {
                                                const [start, end] = update;
                                                setStartDate(start);
                                                setEndDate(end);
                                            }}
                                            inline
                                            className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                            maxDate={new Date()}
                                        />
                                    </div>
                                </div>
                                <div className="flex justify-between mt-6">
                                    <button
                                        className="px-8 py-2 text-white bg-black rounded-full"
                                        onClick={() => setCustomDatePopupOpen(false)}  >
                                        Cancel
                                    </button>

                                    <button
                                        className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                        onClick={handleApplyFilters}

                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>)}

                        {isSearchVisible && (
                            <div ref={searchPopupRef}
                                className="absolute top-14 left-[calc(50%+117px)] transform -translate-x-1/2 mt-1 w-80">
                                <div className="flex items-center border border-gray-300 rounded-3xl bg-white shadow-lg">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/images/desktop/Nav-Search.svg`}
                                        alt="search-lens-icon"
                                        className="w-10 h-10 mx-2"
                                    />
                                    <input
                                        type="text"
                                        placeholder="Search for Team members..."
                                        className="w-full px-4 py-2 text-gray-700 focus:outline-none rounded-3xl"
                                    />
                                </div>
                            </div>
                        )}

                        {isMemberPopupOpen && (
                            <div ref={memberPopupRef} className="absolute top-16 left-1/2 transform -translate-x-1/2 w-[261px] h-[330px] bg-white border rounded-3xl shadow-lg p-4 z-50">
                                <div className="flex justify-between items-center mb-4">
                                    <h3 className="text-[14px] font-normal leading-[20px]">Select User:</h3>
                                    <button className="text-gray-600 hover:text-gray-800" onClick={toggleMemberPopup}>
                                        &times;
                                    </button>
                                </div>
                                <div className="relative mb-4">
                                    <input
                                        type="text"
                                        placeholder="Search user..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full p-2 border rounded-md pr-10"
                                    />
                                    <button
                                        onClick={() => setShowDropdown(!showDropdown)}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2">
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/Navdownarrow.svg`}
                                            alt="dropdown icon"
                                            className="h-6 w-6"
                                        />
                                    </button>
                                    {showDropdown && (
                                        <ul className="absolute top-full left-0 w-full bg-white border rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                                            {filteredOptions
                                                .filter(option =>
                                                    option.name.toLowerCase().includes(searchQuery.toLowerCase())
                                                )
                                                .map((option) => (
                                                    <li
                                                        key={option.id}
                                                        className="p-2 hover:bg-gray-100 cursor-pointer"
                                                        onClick={() => handleMemberSelection(option.id, option.name)}
                                                    >
                                                        {option.name}
                                                    </li>
                                                ))}
                                        </ul>
                                    )}
                                </div>
                                <div className="flex justify-between mt-6">
                                    <button
                                        className="px-8 py-2 text-white bg-black rounded-full"
                                        onClick={toggleMemberPopup}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                        onClick={handleApplyFilters}
                                    >
                                        Apply
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default CallStatus;